<!--
  Copyright (c) 2026 237 Solutions Group Co., Ltd.
  Developed by Patcharapong Ponzue (contact@ibig.dev)

  Credit Package CRUD Page
-->
<template>
  <div>
    <data-sidebar
      :is-sidebar-active.sync="isSidebarActive"
      :data="sidebarData"
      :language="language"
      @refetch-data="refetchData"
      @add="addData"
      @update="updateData"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>{{ $t("Show") }}</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="pageChanged"
            />
            <label>{{ $t("Entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('Search') + '...'"
                @input="typing"
              />
              <b-button
                variant="primary"
                @click="sidebarAddNew()"
              >
                <span class="text-nowrap">{{ $t("Add") }} {{ $t("Credit Package") }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refTable"
        class="position-relative"
        :items="respData"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        show-empty
        :empty-text="$t('No matching records found')"
      >
        <!-- Column: ID -->
        <template #cell(_id)="data">
          <span class="text-muted">
            #{{ data.item._id.slice(-6) }}
          </span>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <span class="font-weight-bold">
            {{ showFromLocale(data.item.name) }}
          </span>
        </template>

        <!-- Column: Credit -->
        <template #cell(credit)="data">
          <b-badge variant="light-success">
            {{ data.item.credit }}
          </b-badge>
        </template>

        <!-- Column: Amount -->
        <template #cell(amount)="data">
          ฿{{ Number(data.item.amount).toLocaleString() }}
        </template>

        <!-- Column: Bonus -->
        <template #cell(bonus)="data">
          <b-badge variant="light-info">
            {{ data.item.bonus || 0 }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="data.item.status ? 'light-success' : 'light-danger'">
            {{ data.item.status ? $t('Active') : $t('Inactive') }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(action)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item @click="sidebarEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t("Edit") }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="deleteData(data.item._id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">{{ $t("Delete") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">{{ currentPage }} / {{ totalPage }} {{ $t("Of") }} {{ totalData }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalData"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @input="pageChanged"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BDropdown,
  BDropdownItem,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DataSidebar from './DataSidebar.vue'

const STORE_MODULE_NAME = 'creditPackage'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    BBadge,
    vSelect,
    DataSidebar,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isSidebarActive: false,
      sidebarData: {},
      currentPage: 1,
      perPage: 10,
      searchQuery: '',
      language: [],
    }
  },
  computed: {
    respData() {
      if (
        store.state[STORE_MODULE_NAME] != null
        && store.state[STORE_MODULE_NAME].respData != null
      ) {
        return store.state[STORE_MODULE_NAME].respData.data
      }
      return []
    },
    totalData() {
      if (
        store.state[STORE_MODULE_NAME] != null
        && store.state[STORE_MODULE_NAME].respData != null
      ) {
        return store.state[STORE_MODULE_NAME].respData.max
      }
      return 0
    },
    totalPage() {
      if (this.totalData > 0) {
        return Math.ceil(this.totalData / this.perPage)
      }
      return 1
    },
    tableColumns() {
      return [
        { key: '_id', label: 'ID', sortable: false },
        { key: 'name', label: this.$t('Name'), sortable: false },
        { key: 'credit', label: this.$t('Credits'), sortable: false },
        { key: 'amount', label: this.$t('Price'), sortable: false },
        { key: 'bonus', label: this.$t('Bonus'), sortable: false },
        { key: 'status', label: this.$t('Status'), sortable: false },
        { key: 'action', label: this.$t('Action'), sortable: false },
      ]
    },
    perPageOptions() {
      return [10, 25, 50, 100]
    },
  },
  created() {
    // Register Vuex module
    if (!store.hasModule(STORE_MODULE_NAME)) {
      store.registerModule(STORE_MODULE_NAME, require('@/store/services/eCommerce/creditPackage').default)
    }
    // Get language from app store
    const systemConfig = store.state.appConfig ? store.state.appConfig.systemConfig : null
    if (systemConfig && systemConfig.language) {
      this.language = systemConfig.language
    }
    this.fetchData()
  },
  methods: {
    showFromLocale(data) {
      if (data == null) return ''
      if (typeof data === 'string') return data
      // Handle [{lang, value}] array format from DB
      if (Array.isArray(data)) {
        const locale = this.$i18n.locale
        const found = data.find(d => d.lang === locale)
        if (found) return found.value
        if (data.length > 0 && data[0].value) return data[0].value
        return ''
      }
      // Handle {locale: value} object format
      const locale = this.$i18n.locale
      if (data[locale]) return data[locale]
      const keys = Object.keys(data)
      if (keys.length > 0) return data[keys[0]]
      return ''
    },
    fetchData() {
      store.dispatch(`${STORE_MODULE_NAME}/get`, {
        pageSize: this.perPage,
        currentPage: this.currentPage - 1,
        searchQuery: this.searchQuery,
      }).catch(error => {
        console.log(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
    },
    refetchData() {
      this.fetchData()
    },
    pageChanged() {
      this.fetchData()
    },
    typing() {
      this.currentPage = 1
      this.fetchData()
    },
    sidebarAddNew() {
      this.sidebarData = {}
      this.isSidebarActive = true
    },
    sidebarEdit(data) {
      this.sidebarData = data
      this.isSidebarActive = true
    },
    addData(data) {
      store.dispatch(`${STORE_MODULE_NAME}/add`, data)
        .then(() => {
          this.isSidebarActive = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Added Successfully'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    updateData(data) {
      store.dispatch(`${STORE_MODULE_NAME}/update`, data)
        .then(() => {
          this.isSidebarActive = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Updated Successfully'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Error'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    deleteData(id) {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t("You won't be able to revert this!"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch(`${STORE_MODULE_NAME}/remove`, id)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('Deleted'),
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
            })
            .catch(error => {
              console.log(error)
            })
        }
      })
    },
  },
}
</script>
